import { Suspense, type ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { BoomCrmPaywall } from "./BoomCrmPaywall";
import { PAYWALL_COPY, type PaywallFeature } from "./constants";

interface LockedPaywallPopoverProps {
  feature: PaywallFeature;
  children: ReactNode;
  /**
   * Classes for the span the tooltip anchors to. The default stretches it so a
   * wrapped tile or card fills its grid cell like its unlocked siblings. For a
   * lone button in a flex row (e.g. a page header with `justify-between`) pass
   * `"inline-block"` instead: a full-width span there grabs the whole row,
   * squeezes the heading onto two lines and drags the button to the left.
   */
  triggerClassName?: string;
}

/**
 * Hover popover holding the full paywall card, for a tile that stays visible but
 * locked (Figma BoomCRM-Paywall 34:24027). `LockedTooltip` is the one-line
 * variant; this one is for the places the design shows the whole card with its
 * "Book a demo" button.
 *
 * `interactive` plus a close delay so the pointer can travel from the tile into
 * the card and press that button without it closing — same arrangement
 * `BaseTabs` uses for its disabled-tab tooltips.
 *
 * Placed on the vertical axis on purpose. The card is ~630px wide (its preview
 * frame holds a fixed 583px inner card), and the tiles that use this sit inside
 * a centred modal — so a left/right placement has nowhere to go and ran 230px
 * off-screen at 1280px wide, putting "Book a demo" out of reach. Anchoring
 * below the tile gives the card the whole viewport width to shift within.
 */
export const LockedPaywallPopover = ({
  feature,
  children,
  triggerClassName = "block w-full"
}: LockedPaywallPopoverProps) => {
  return (
    <Suspense fallback={children}>
      <Tooltip
        placement="bottom-start"
        interactive
        delay={{ open: 100, close: 150 }}
      >
        <TooltipTrigger asChild>
          <span className={triggerClassName}>{children}</span>
        </TooltipTrigger>
        {/* Width is capped on this wrapper rather than passed to the card: the
            card already carries a max-w utility, and a second one on the same
            element is a coin toss over which rule the stylesheet emits last.
            No chrome: the card is its own surface, and the primitive's dark
            square bubble showed through as black corner triangles behind the
            card's rounded corners. */}
        <TooltipContent
          chrome={false}
          className="z-[100] max-w-[calc(100vw-2rem)]"
        >
          <BoomCrmPaywall {...PAYWALL_COPY[feature]} />
        </TooltipContent>
      </Tooltip>
    </Suspense>
  );
};
